import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { Link, useOutletContext } from 'react-router'

import { ErrorDeLaApi, pedir } from '../api/nucleo'
import { Aviso, Boton, Casilla, claseDeEntrada, Esqueleto, Pastilla, Tarjeta, Titulo } from '../componentes/ui'
import type { Contexto } from './Modulo'

/**
 * «Revisión semanal» (CLAUDE.md §35): cada semana se revisan las páginas publicadas más flojas en SEO y
 * la IA propone un título y una descripción para cada una. Aquí se ven, se retocan si hace falta y se
 * aceptan con un clic (con los permisos de quien pulsa) o se descartan. Va incluida en el plan: no gasta
 * créditos. Nada se cambia sin que alguien lo acepte.
 */
type Propuesta = {
  id: string
  coleccion: string
  documento: number
  nombre: string
  ruta: string
  puntuacion: number
  fallos: string[]
  actual: { titulo: string; descripcion: string }
  propuesta: { titulo: null | string; descripcion: null | string }
  estado: 'aceptada' | 'descartada' | 'pendiente'
}
type Resumen = { id: number; semana: string; estado: string; fecha: string; pendientes: number; aceptadas: number; total: number }
type Respuesta = { activa: boolean; semanaActual: string; ultima: null | (Resumen & { propuestas: Propuesta[] }); anteriores: Resumen[] }

const EDITORES: Record<string, string> = { paginas: 'paginas', entradas: 'entradas', productos: 'productos', servicios: 'servicios' }

const Tarjeta1 = ({ p, revision, sitio, base, hecho }: { p: Propuesta; revision: number; sitio: number; base: string; hecho: () => void }) => {
  const [titulo, setTitulo] = useState(p.propuesta.titulo ?? '')
  const [descripcion, setDescripcion] = useState(p.propuesta.descripcion ?? '')
  const [enviando, setEnviando] = useState<null | string>(null)
  const [fallo, setFallo] = useState<null | string>(null)
  const resolver = async (accion: 'aceptar' | 'descartar') => {
    setEnviando(accion)
    setFallo(null)
    try {
      await pedir(`/panel/revision/${accion}`, { metodo: 'POST', cuerpo: { sitio, revision, propuesta: p.id, ...(accion === 'aceptar' ? { titulo, descripcion } : {}) } })
      hecho()
    } catch (e) {
      setFallo(e instanceof ErrorDeLaApi ? e.message : 'No se ha podido guardar. Vuelve a intentarlo.')
    } finally {
      setEnviando(null)
    }
  }
  const conIA = Boolean(p.propuesta.titulo || p.propuesta.descripcion)
  const resuelta = p.estado !== 'pendiente'
  return (
    <Tarjeta>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-base font-semibold">{p.nombre || p.ruta}</h3>
          <p className="text-[13px] text-tenue">
            {p.ruta} · {p.puntuacion}/100 en SEO
          </p>
        </div>
        <div className="flex items-center gap-2">
          {resuelta && <Pastilla clave={p.estado === 'aceptada' ? 'publicado' : 'neutra'}>{p.estado === 'aceptada' ? 'Aceptada' : 'Descartada'}</Pastilla>}
          {EDITORES[p.coleccion] && (
            <Link className="inline-flex items-center gap-1 text-[13px] text-violeta hover:underline" to={`${base}/${EDITORES[p.coleccion]}/${p.documento}`}>
              Abrir <ExternalLink aria-hidden className="size-3.5" />
            </Link>
          )}
        </div>
      </div>
      {p.fallos.length > 0 && (
        <ul className="mt-3 list-disc pl-5 text-[13px] text-tinta-2">
          {p.fallos.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      )}
      {conIA && !resuelta ? (
        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium">Título para Google</span>
            <input className={claseDeEntrada()} maxLength={120} onChange={(e) => setTitulo(e.target.value)} value={titulo} />
            <small className="text-xs text-tenue">Ahora: {p.actual.titulo || '(sin título propio)'} · {titulo.length} caracteres</small>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium">Descripción</span>
            <textarea className={`${claseDeEntrada()} min-h-20 py-2`} maxLength={320} onChange={(e) => setDescripcion(e.target.value)} value={descripcion} />
            <small className="text-xs text-tenue">Ahora: {p.actual.descripcion || '(sin descripción)'} · {descripcion.length} caracteres</small>
          </label>
          {fallo && <Aviso tono="error">{fallo}</Aviso>}
          <div className="flex flex-wrap gap-2">
            <Boton cargando={enviando === 'aceptar'} disabled={enviando !== null} onClick={() => void resolver('aceptar')} pequeno tipo="primario">
              Aceptar y guardar
            </Boton>
            <Boton cargando={enviando === 'descartar'} disabled={enviando !== null} onClick={() => void resolver('descartar')} pequeno>
              Descartar
            </Boton>
          </div>
        </div>
      ) : (
        !resuelta && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <p className="text-[13px] text-tenue">No hemos podido proponer un texto para esta página: ábrela y mejora lo que se indica arriba.</p>
            <Boton disabled={enviando !== null} onClick={() => void resolver('descartar')} pequeno tipo="discreto">
              Ya está
            </Boton>
          </div>
        )
      )}
    </Tarjeta>
  )
}

export const Revision = () => {
  const { sitio, espacio, permisos } = useOutletContext<Contexto>()
  const cliente = useQueryClient()
  const clave = ['revision', sitio?.id]
  const { data, isPending, error } = useQuery({ queryKey: clave, enabled: Boolean(sitio), queryFn: () => pedir<Respuesta>(`/panel/revision?sitio=${sitio!.id}`) })
  const [cambiando, setCambiando] = useState(false)
  if (!sitio) return <Aviso tono="error">Esa web no existe, o no es tuya.</Aviso>
  const base = `/e/${espacio}/s/${sitio.id}`
  const refrescar = () => void cliente.invalidateQueries({ queryKey: clave })
  const titulo = (
    <Titulo descripcion="Cada semana revisamos tus páginas publicadas más flojas en Google y te proponemos un título y una descripción mejores. No cambiamos nada sin que lo aceptes, y no gasta créditos." rotulo={sitio.nombre}>
      Revisión semanal
    </Titulo>
  )
  if (isPending) return (<>{titulo}<Esqueleto /></>)
  if (error) return (<>{titulo}<Aviso tono="error">No hemos podido leer la revisión. Vuelve a intentarlo en un momento.</Aviso></>)

  const cambiarActiva = async (activa: boolean) => {
    setCambiando(true)
    try {
      await pedir('/panel/revision/activa', { metodo: 'POST', cuerpo: { sitio: sitio.id, activa } })
      refrescar()
    } finally {
      setCambiando(false)
    }
  }
  const ultima = data.ultima
  const pendientes = ultima?.propuestas.filter((p) => p.estado === 'pendiente') ?? []
  const resueltas = ultima?.propuestas.filter((p) => p.estado !== 'pendiente') ?? []
  return (
    <>
      {titulo}
      <div className="flex flex-col gap-6">
        <Tarjeta>
          <label className="flex items-center gap-3">
            <Casilla disabled={cambiando || !permisos.tiene('seo.sitio')} marcada={data.activa} onChange={(e) => void cambiarActiva(e.target.checked)} />
            <span className="text-sm">
              <strong>Revisar mi web cada semana</strong>
              <span className="block text-[13px] text-tenue">{data.activa ? 'Recibirás un correo cuando haya propuestas.' : 'Pausada: no se revisará hasta que la vuelvas a encender.'}</span>
            </span>
          </label>
        </Tarjeta>

        {!ultima ? (
          <Tarjeta>
            <div className="flex items-center gap-3">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-violeta-suave text-violeta">
                <Sparkles className="size-5" />
              </span>
              <p className="text-[13px] text-tinta-2">Todavía no hay ninguna revisión. Cuando tu web esté publicada, la primera llegará en unos días y te avisaremos por correo.</p>
            </div>
          </Tarjeta>
        ) : ultima.estado === 'en-curso' ? (
          <Aviso tono="info">Estamos haciendo la revisión de esta semana. Vuelve en unos minutos.</Aviso>
        ) : ultima.total === 0 ? (
          <Aviso tono="info">Esta semana no hay nada que mejorar: todas tus páginas publicadas puntúan bien en SEO.</Aviso>
        ) : (
          <>
            <h2 className="font-display text-lg font-semibold">
              Semana {ultima.semana.split('-W')[1]} · {pendientes.length > 0 ? `${pendientes.length} por revisar` : 'todo revisado'}
            </h2>
            {pendientes.map((p) => (
              <Tarjeta1 base={base} hecho={refrescar} key={p.id} p={p} revision={ultima.id} sitio={sitio.id} />
            ))}
            {resueltas.map((p) => (
              <Tarjeta1 base={base} hecho={refrescar} key={p.id} p={p} revision={ultima.id} sitio={sitio.id} />
            ))}
          </>
        )}

        {data.anteriores.length > 0 && (
          <Tarjeta>
            <h2 className="font-display text-base font-semibold">Semanas anteriores</h2>
            <ul className="mt-2 divide-y divide-linea text-[13px]">
              {data.anteriores.map((r) => (
                <li className="flex justify-between py-1.5" key={r.id}>
                  <span>Semana {r.semana.split('-W')[1]}</span>
                  <span className="text-tenue">
                    {r.aceptadas} de {r.total} aceptadas
                  </span>
                </li>
              ))}
            </ul>
          </Tarjeta>
        )}
      </div>
    </>
  )
}
